import { RotateCcw, HomeIcon } from "lucide-react";
import { AlertIcon } from "../icons/alert-icon";
import { PrimaryButton } from "../ui/primary-button";
import { SecondaryButton } from "../ui/secondary-button";

interface ErrorFallbackProps {
  error?: Error;
  onRetry?: () => void;
  onNavigateHome?: () => void;
  title?: string;
  description?: string;
  message?: string; // Alternative to description for backwards compatibility
}

export function ErrorFallback({
  error,
  onRetry,
  onNavigateHome,
  title = "Something went wrong",
  description,
  message = "An unexpected error occurred. Please try again.",
}: ErrorFallbackProps) {
  const showErrorDetails = import.meta.env.DEV && error;
  const displayMessage = description || message;

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <AlertIcon className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">{title}</h2>
          <p className="text-muted-foreground mb-4">{displayMessage}</p>
          
          {showErrorDetails && (
            <details className="text-left bg-muted p-4 rounded-lg mb-4">
              <summary className="cursor-pointer font-medium">Error Details</summary>
              <pre className="mt-2 text-sm whitespace-pre-wrap break-words">
                {error.message}
                {error.stack}
              </pre>
            </details>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && (
            <PrimaryButton onClick={onRetry} className="flex items-center gap-2 cursor-pointer">
              <RotateCcw className="h-4 w-4" />
              Try Again
            </PrimaryButton>
          )}
          {onNavigateHome && (
            <SecondaryButton onClick={onNavigateHome} className="flex items-center gap-2 cursor-pointer">
              <HomeIcon className="h-4 w-4" />
              Go to Home
            </SecondaryButton>
          )}
        </div>
      </div>
    </div>
  );
}